import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ImageOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DASHBOARD_SCREENSHOTS } from "@/lib/content";
import { SectionHeading } from "./SectionHeading";

export const DashboardShowcase = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(DASHBOARD_SCREENSHOTS[0].key);
  const [failed, setFailed] = useState({});
  const current = DASHBOARD_SCREENSHOTS.find((s) => s.key === active) || DASHBOARD_SCREENSHOTS[0];
  const title = t(`content.${current.tk}.title`);
  const desc = t(`content.${current.tk}.desc`);
  const hasFailed = !!failed[current.key];
  return (
    <section data-testid="dashboard-showcase-section" className="bg-slate-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading
          eyebrow={t("sections.dashboard.eyebrow")}
          title={t("sections.dashboard.title")}
          description={t("sections.dashboard.desc")}
          align="center"
          testIdPrefix="dashboard-showcase"
        />
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2" data-testid="dashboard-tab-list">
          {DASHBOARD_SCREENSHOTS.map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid={`dashboard-tab-${s.key}`}
              onClick={() => setActive(s.key)}
              className={`inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                active === s.key
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              }`}
            >
              {t(`content.${s.tk}.title`)}
            </button>
          ))}
        </div>
        <motion.div
          key={current.key}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 grid gap-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 lg:grid-cols-5 lg:items-center lg:p-8"
          data-testid={`dashboard-screenshot-${current.key}`}
        >
          <div className="lg:col-span-3">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {hasFailed ? (
                <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-slate-100 p-6 text-slate-500" data-testid={`dashboard-screenshot-fallback-${current.key}`}>
                  <ImageOff className="h-8 w-8" aria-hidden />
                  <p className="text-sm font-semibold text-slate-700">{title}</p>
                  <p className="text-xs">Preview image unavailable.</p>
                </div>
              ) : (
                <picture>
                  {current.fallback && <source srcSet={current.src} type="image/webp" />}
                  <img
                    src={current.fallback || current.src}
                    alt={`DC Dashboard screenshot — ${title}`}
                    width={current.width || 1200}
                    height={current.height || 675}
                    loading="lazy"
                    decoding="async"
                    onError={() => setFailed((prev) => ({ ...prev, [current.key]: true }))}
                    data-testid={`dashboard-screenshot-img-${current.key}`}
                    style={current.cropLeft ? { marginLeft: `-${current.cropLeft}%`, width: `${100 + current.cropLeft}%`, maxWidth: "none" } : undefined}
                    className="h-auto w-full object-cover"
                  />
                </picture>
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">DC Dashboard</div>
            <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{desc}</p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              {[
                t("content.featureBullets.liveData"),
                t("content.featureBullets.auditExports"),
                t("content.featureBullets.multiCurrency"),
                t("content.featureBullets.roleAccess"),
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
